"use client";

import * as React from "react";
import Image from "next/image";
import {
    Box,
    Chip,
    Container,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import Heading from "../heading";
import IconButton from "@mui/material/IconButton";
import { getOurFleetsListNew } from "../../redux/slices/common";
import { useDispatch, useSelector } from '../../redux/store'
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useRouter } from "next/navigation";
import CtaButton from "../buttonWithArrow";

/* ---------- Card (mobile-scaled) ---------- */
function FleetCardResponsive({ name, image, year, transmission, fuelType, capacity, model_id }) {
    const router = useRouter()
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #eceff2",
                boxShadow: "0 10px 28px rgba(0,0,0,.12)",
                bgcolor: "#fff",
            }}
        >
            {/* image block with rounded inner corners */}
            <Box sx={{ position: "relative", height: { xs: 180, sm: 210, md: 270 }, p: 1.25 }}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 10,
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    <Image src={`${process.env.IMAGE_BASE_URL}/${image}`} alt={name} fill sizes="(max-width: 600px) 100vw, 25vw"
                        style={{ objectFit: "cover" }}
                    />
                </Box>
                <IconButton
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: { xs: 34, md: 40 },
                        height: { xs: 34, md: 40 },
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        border: "1px solid #e6eaee",
                        boxShadow: "0 2px 10px rgba(0,0,0,.12)",
                    }}
                    onClick={() => {
                        window.location.href = `/carBooking/carBookRide?modelIdget=${model_id}`;
                    }}
                >
                    <ArrowOutwardIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                </IconButton>
            </Box>

            {/* title */}
            <Typography
                sx={{
                    px: { xs: 2, md: 3 },
                    pt: 1,
                    pb: 1.25,
                    fontWeight: 800,
                    textAlign: "start",
                    fontSize: { xs: 16, md: 20 },
                    color: "#111",
                }}
            >
                {name}
            </Typography>

            {/* specs bar */}
            <Paper
                elevation={0}
                sx={{
                    mx: { xs: 1.5, md: 2 },
                    mb: { xs: 1.5, md: 2 },
                    px: { xs: 1, md: 1 },
                    py: { xs: 0.5, md: 0.5 },
                    borderRadius: 3,
                    bgcolor: "#f6f6f6",
                    display: "flex",
                    justifyContent: { xs: "space-around", md: "space-between" },
                    alignItems: "center",
                    gap: { xs: 1, md: 0 },
                }}
            >
                <Spec icon="/img/ourfleets/speedometer-01.svg" label={year} />
                <Spec icon="/img/ourfleets/manual-gearbox.svg" label={transmission} />
                <Spec icon="/img/ourfleets/users-01.svg" label={capacity} />
                <Spec icon="/img/ourfleets/gas-station.svg" label={fuelType} />
            </Paper>
        </Paper>
    );
}

function FleetCardSkeleton() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #eceff2",
                boxShadow: "0 10px 28px rgba(0,0,0,.12)",
                bgcolor: "#fff",
            }}
        >
            {/* image block */}
            <Box sx={{ position: "relative", height: { xs: 180, sm: 210, md: 270 }, p: 1.25 }}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="100%"
                    sx={{ borderRadius: 3 }}
                />
            </Box>

            {/* title */}
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 1, pb: 1.25 }}>
                <Skeleton variant="text" width="70%" height={28} />
            </Box>

            {/* specs bar */}
            <Paper
                elevation={0}
                sx={{
                    mx: { xs: 1.5, md: 2 },
                    mb: { xs: 1.5, md: 2 },
                    px: { xs: 1, md: 1 },
                    py: { xs: 0.5, md: 0.5 },
                    borderRadius: 3,
                    bgcolor: "#f6f6f6",
                    display: "flex",
                    justifyContent: { xs: "space-around", md: "space-between" },
                    alignItems: "center",
                    gap: { xs: 1, md: 0 },
                }}
            >
                {Array.from({ length: 4 }).map((_, i) => (
                    <Stack key={i} alignItems="center" spacing={0.75} sx={{ minWidth: { xs: 52, md: 56 } }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="text" width={32} height={16} />
                    </Stack>
                ))}
            </Paper>
        </Paper>
    );
}

export default function PickYourCarGrid({ data }) {
    const dispatch = useDispatch();
    const { title, subtitle, filters, carData, type, options, button } = data;
    const [filter, setFilter] = React.useState("Popular Car");
    const [serviceList, setServiceList] = React.useState([]);
    const [loader, setLoader] = React.useState(false);
    const { ourFleetsRes } = useSelector(
        state => state.common
    );

    React.useEffect(() => {
        setLoader(true);
        dispatch(getOurFleetsListNew());
    }, [dispatch])

    // handle response
    React.useEffect(() => {
        if (!ourFleetsRes) return;      // wait until we have a response
        setLoader(false);                        // stop loading no matter success/fail

        if (ourFleetsRes.status === 1) {
            setServiceList(ourFleetsRes?.data ?? []);
        } else {
            setServiceList([]);
        }
    }, [ourFleetsRes]);

    return (
        <Box sx={{ py: { xs: 6, md: 4 }, bgcolor: "#f8f8f8" }}>
            <Container sx={{ maxWidth: "1200px !important" }}>
                {/* Heading */}
                <Heading title={title} subtitle={subtitle} mb={6} />

                {/* Toggle */}
                {/* Responsive Toggle */}
                <Box
                    sx={{
                        overflowX: "auto",
                        mx: { xs: -2, sm: 0 },
                        px: { xs: 2, sm: 0 },
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: { xs: "column-reverse", md: "row" }
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="center"
                        flexWrap="wrap"
                        sx={{
                            gap: { xs: 1, sm: 1.25, md: 1.5 }
                        }}
                    >
                        {filters?.map((t, index) => (
                            <Chip
                                key={index}
                                clickable
                                onClick={() => setFilter(t)}
                                label={t}
                                sx={{
                                    ...pillSx(filter === t),
                                    px: { xs: 1.25, sm: 1.5, md: 1.75 },
                                    height: { xs: 36, sm: 36, md: 36 },
                                    borderRadius: 999,
                                    "& .MuiChip-label": {
                                        fontWeight: 600,
                                        fontSize: { xs: 12, sm: 13, md: 14 },
                                    },
                                }}
                            />
                        ))}
                    </Stack>
                </Box>

                {/* Grid Layout */}
                <Box sx={{ mt: 4 }}>
                    <Grid container spacing={3} justifyContent="start">
                        {loader
                            ? Array.from({ length: 6 }).map((_, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <FleetCardSkeleton />
                                </Grid>
                            ))
                            : serviceList.filter((car) => (filter === "Popular Car" || !filter) ? true : car.type === filter).map((car, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <FleetCardResponsive {...car} />
                                </Grid>
                            ))}
                    </Grid>
                </Box>

                {
                    button &&
                    <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                        <CtaButton label={button?.text ?? "Book a Call"} href={button?.href} />
                    </Box>
                }


            </Container>
        </Box>
    );
}

const pillSx = (active) => ({
    flexShrink: 0,
    fontWeight: 700,
    minWidth: "150px",
    borderRadius: "999px",
    px: { xs: 1.5, md: 2.5 },
    py: { xs: 1, md: 3 },
    fontSize: { xs: 14, md: 16 },
    background: active ? "linear-gradient(180deg, #75c4c5 0%, #0d8196 100%)" : "#f1f1f1",
    color: active ? "#fff" : "#000",
    "&:hover": {
        background: active ? "linear-gradient(180deg, #4bb6b4 0%, #249f9c 100%)" : "#f7f9f9",
    },
    "& .MuiChip-label": { px: 0, fontWeight: 700 },
});


function Spec({ icon, label }) {
    const isImage = typeof icon === "string";

    return (
        <Stack
            alignItems="center"
            spacing={0.75}
            sx={{ minWidth: { xs: 52, md: 56 } }}
        >
            <Box
                sx={{
                    width: { xs: 28, md: 28 },
                    height: { xs: 28, md: 28 },
                    display: "grid",
                    placeItems: "center",
                }}
            >
                {isImage ? (
                    <Image
                        src={icon}
                        alt={label}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                    />
                ) : (
                    icon
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: { xs: 12, md: 12 },
                    color: "#333",
                    fontWeight: 600,
                }}
            >
                {label}
            </Typography>
        </Stack>
    );
}
